/**
 * /contact — Contact page. Structured contact information, calm voice.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { MarketingLayout } from '../components/layouts/marketing-layout';

export const Route = createFileRoute('/contact')({
  component: ContactPage,
});

function ContactPage() {
  return (
    <MarketingLayout>
      <div
        style={{
          maxWidth: 960,
          margin: '0 auto',
          padding: '72px 48px 96px',
        }}
      >
        {/* Beno feedback (2026-07-15): a clearly visible way back to the
            marketing home, in addition to the header wordmark link. */}
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--primary-600)',
            textDecoration: 'none',
            marginBottom: 24,
          }}
        >
          <span aria-hidden="true">←</span> Home
        </Link>

        {/* Heading */}
        <div style={{ marginBottom: 56 }}>
          <p className="eyebrow" style={{ marginBottom: 16 }}>
            Contact
          </p>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(40px, 6vw, 72px)',
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              margin: 0,
            }}
          >
            We answer email.
          </h1>
        </div>

        {/* Two columns */}
        <div className="contact-grid" style={{ alignItems: 'start' }}>
          {/* Left: structured contact info */}
          <div>
            <div className="card" style={{ padding: '32px 28px' }}>
              <div className="eyebrow" style={{ marginBottom: 24 }}>
                Elreign Legacy (Pty) Ltd
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <ContactRow label="Registration number" value="2013/108006/07" />
                <ContactRow label="Information Officer" value="Paul Prinsloo" />
                <ContactRow
                  label="General enquiries"
                  value={
                    <a
                      href="mailto:hello@elreign.io"
                      style={{ color: 'var(--primary-600)', fontFamily: 'var(--mono)', fontSize: 14 }}
                    >
                      hello@elreign.io
                    </a>
                  }
                />
                <ContactRow
                  label="Security"
                  value={
                    <div>
                      <a
                        href="mailto:security@elreign.io"
                        style={{ color: 'var(--primary-600)', fontFamily: 'var(--mono)', fontSize: 14, display: 'block' }}
                      >
                        security@elreign.io
                      </a>
                      <span
                        className="mono"
                        style={{ fontSize: 11.5, color: 'var(--ink-4)', display: 'block', marginTop: 4 }}
                      >
                        PGP fingerprint: TBD on launch
                      </span>
                    </div>
                  }
                />
                <ContactRow
                  label="Postal address"
                  value={
                    <span style={{ lineHeight: 1.6 }}>
                      Cape Town, South Africa
                      <br />
                      <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>
                        Physical address published on company registration.
                      </span>
                    </span>
                  }
                />
              </div>
            </div>
          </div>

          {/* Right: small note */}
          <div style={{ paddingTop: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <p
                style={{
                  fontSize: 19,
                  lineHeight: 1.65,
                  color: 'var(--ink-2)',
                  margin: 0,
                }}
              >
                We are a small team. We answer email within one business day. If your question
                is urgent and security-related, use the PGP-encrypted address.
              </p>

              <p
                style={{
                  fontSize: 16,
                  lineHeight: 1.65,
                  color: 'var(--ink-3)',
                  margin: 0,
                }}
              >
                For PAIA information access requests, use the PAIA contact above and follow the
                process described in the{' '}
                <a
                  href="/legal/paia"
                  style={{ color: 'var(--primary-600)' }}
                >
                  PAIA manual
                </a>
                .
              </p>

              <p
                style={{
                  fontSize: 16,
                  lineHeight: 1.65,
                  color: 'var(--ink-3)',
                  margin: 0,
                }}
              >
                Fiduciary firms interested in deploying Elreign for their clients should write
                to{' '}
                <a
                  href="mailto:hello@elreign.io"
                  style={{ color: 'var(--primary-600)', fontFamily: 'var(--mono)', fontSize: 14 }}
                >
                  hello@elreign.io
                </a>
                {' '}with a brief description of your firm and client base.
              </p>

              <div
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--hairline)',
                  borderRadius: 'var(--r-md)',
                  padding: '20px 22px',
                  borderLeft: '3px solid var(--accent-400)',
                }}
              >
                <p
                  className="serif"
                  style={{
                    fontStyle: 'italic',
                    fontSize: 18,
                    color: 'var(--ink)',
                    margin: 0,
                    lineHeight: 1.4,
                  }}
                >
                  We do not do sales calls. We do have conversations. There is a difference.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}

interface ContactRowProps {
  label: string;
  value: React.ReactNode;
}

function ContactRow({ label, value }: ContactRowProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-4)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.45 }}>
        {value}
      </span>
    </div>
  );
}
